/** Schema metadata stays ordinary JSON. Only scalars JSON cannot distinguish
 * receive explicit paths; untyped state has its separate tagged encoding. */
export type MetadataScalar = [path: string[], kind: 'undefined' | '-0'];
const MAX_DEPTH = 128;
const MAX_PATH_PARTS = 2_000_000;

export function metadataScalars(
  root: unknown,
  encodedStates: WeakSet<object>,
  canonical = false,
): MetadataScalar[] | undefined {
  const scalars: MetadataScalar[] = [];
  const path: string[] = [];
  let parts = 0;
  const visit = (value: unknown): void => {
    if (value === undefined || Object.is(value, -0)) {
      if (!path.length || (parts += path.length) > MAX_PATH_PARTS)
        throw new RangeError('Current metadata scalar paths exceed limit');
      scalars.push([[...path], value === undefined ? 'undefined' : '-0']);
      return;
    }
    if (!value || typeof value !== 'object' || encodedStates.has(value)) return;
    if (path.length >= MAX_DEPTH) throw new RangeError('Current metadata is too deep');
    const keys = Object.keys(value);
    if (canonical) keys.sort();
    for (const key of keys) {
      path.push(key);
      visit((value as Record<string, unknown>)[key]);
      path.pop();
    }
  };
  visit(root);
  return scalars.length ? scalars : undefined;
}

/** Restore only a missing property or a JSON-normalized scalar. All path hops
 * must be own properties; __proto__ is never assigned through a setter. */
export function restoreMetadataScalars(root: unknown, input: unknown): MetadataScalar[] {
  if (input === undefined) return [];
  if (!Array.isArray(input) || input.length > MAX_PATH_PARTS)
    throw new TypeError('Invalid current metadata scalar paths');
  const restore = createMetadataScalarRestorer(root);
  for (const entry of input) restore(entry);
  return input as MetadataScalar[];
}

/** One private decode's cumulative validation/restoration state. Call only after
 * wire-level validation; partial results are never published if a later row fails. */
export function createMetadataScalarRestorer(root: unknown) {
  let parts = 0;
  return (entry: unknown): MetadataScalar => {
    if (
      !Array.isArray(entry) ||
      entry.length !== 2 ||
      !Array.isArray(entry[0]) ||
      entry[0].length < 1 ||
      entry[0].length > MAX_DEPTH ||
      !entry[0].every((key: unknown) => typeof key === 'string') ||
      (entry[1] !== 'undefined' && entry[1] !== '-0') ||
      (parts += entry[0].length) > MAX_PATH_PARTS
    )
      throw new TypeError('Invalid current metadata scalar path');
    const [path, kind] = entry as MetadataScalar;
    if (!isMetadataPath(path))
      throw new TypeError('Current scalar path is outside metadata');
    const parent = scalarParent(root, path);
    const key = path[path.length - 1];
    if (kind === 'undefined') {
      if (
        Array.isArray(parent)
          ? !Object.hasOwn(parent, key) || parent[key as any] !== null
          : Object.hasOwn(parent, key)
      )
        throw new TypeError('Current metadata scalar would overwrite a value');
    } else if (!Object.hasOwn(parent, key) || !Object.is((parent as any)[key], 0)) {
      throw new TypeError('Invalid current metadata negative zero');
    }
    Object.defineProperty(parent, key, {
      value: kind === 'undefined' ? undefined : -0,
      enumerable: true,
      writable: true,
      configurable: true,
    });
    return entry as MetadataScalar;
  };
}

function isMetadataPath(path: string[]): boolean {
  if (path[0] !== 'chips') return true;
  // Runtime node descriptors and values belong to the block/state codecs.
  const instance =
    path[2] === 'editingInstanceDto' ? 3 : path[2] === 'chipInstanceDtos' ? 4 : -1;
  if (
    instance >= 0 &&
    path[instance] === 'logicGraphDto' &&
    path[instance + 1] === 'nodes'
  )
    return false;
  // This includes primitive tile states, which cannot appear in a WeakSet.
  return !(
    (path[2] === 'logicCircuitDto' || path[2] === 'structureCircuitDto') &&
    path[3] === 'tileManagerDto' &&
    path[4] === 'tiles' &&
    path[6] === 'state'
  );
}

function scalarParent(
  root: unknown,
  path: string[],
): Record<string, unknown> | unknown[] {
  let value: any = root;
  for (const key of path.slice(0, -1)) {
    if (!value || typeof value !== 'object' || !Object.hasOwn(value, key))
      throw new TypeError('Invalid current metadata scalar owner');
    value = value[key];
  }
  if (!value || typeof value !== 'object')
    throw new TypeError('Invalid current metadata scalar owner');
  const key = path[path.length - 1];
  if (
    Array.isArray(value) &&
    (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= value.length)
  )
    throw new TypeError('Invalid current metadata array index');
  return value;
}

/** Schema validation must retain every restored property, rather than silently
 * accepting an extra path which the schema subsequently discards. */
export function assertMetadataScalars(root: unknown, scalars: MetadataScalar[]): void {
  for (const [path, kind] of scalars) {
    const parent = scalarParent(root, path);
    const key = path[path.length - 1];
    if (
      !Object.hasOwn(parent, key) ||
      !Object.is((parent as any)[key], kind === 'undefined' ? undefined : -0)
    )
      throw new TypeError('Current metadata scalar is outside the schema');
  }
}
